import React, { useState, useMemo, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import Loader from '../../components/Loader';
import { useHttpGetDeviceInfo, useHttpGetDeviceEvents, useHttpGetDeviceDeliveryReports } from '../../hooks';
import withUrlParam from '../../hocs/withUrlParam';
import DeliveryChart from '../Batteries/charts/DeliveryChart';
import EnduranceChart from '../Batteries/charts/EnduranceChart';
import { DELIVERY_SERIES_COLOR, STATE_GREY_SWATCH_OOS, STATE_GREY_SWATCH_UNMEASURED } from '../Batteries/lib/chartTheme';
import { deviceDeliveredFromWire } from '../Batteries/lib/deliveredWire';
import { toRows } from '../Batteries/lib/deliveryChartRows';
import { fcrActivatedSummary, marketsWithCommitment } from '../Batteries/lib/marketReportsV1';
import { CAPACITY_MARKETS, fmtCapacityMw } from '../Batteries/lib/prequalifiedCapacity';

const VbattDeviceSafetyWrapper = ({ paramValue }) => {
    const id = paramValue;
    // The window is computed once per mount, not per render: the hooks fetch on mount only,
    // and the charts' x axis must span exactly the window that was fetched.
    const rangeRef = useRef(null);
    if (rangeRef.current === null) {
        rangeRef.current = {
            from: DateTime.utc().minus({ days: 1 }).toISO(),
            to: DateTime.utc().toISO(),
        };
    }
    const deliveryReportsDateTimeRange = rangeRef.current;

    const { data: info, isReady: deviceIsReady } = useHttpGetDeviceInfo(id);
    const { data: lifeEvents, isReady: lifeEventsAreReady } = useHttpGetDeviceEvents(id);
    const { data: reports, isReady: reportsAreReady } = useHttpGetDeviceDeliveryReports(
        id,
        deliveryReportsDateTimeRange.from,
        deliveryReportsDateTimeRange.to
    );

    if (!deviceIsReady || !lifeEventsAreReady || !reportsAreReady) {
        return <Loader />;
    }

    const device = { id, info, lifeEvents };

    return (
        <div className="card-wrapper">
            <VirtualBattery device={device} reports={reports} reportRange={deliveryReportsDateTimeRange} />
        </div>
    );
};

const ConnectionStatus = ({ connected }) => (
    <span className={connected ? 'status-connected' : 'status-disconnected'}>
        <i className={`fa ${connected ? 'fa-check-circle' : 'fa-times-circle'}`} aria-hidden="true" /> {connected ? 'Connected' : 'Disconnected'}
    </span>
);

const MeasurementsDisplay = ({ measurements }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleMainSection = () => {
        setIsExpanded(prev => !prev);
    };

    if (!measurements || Object.keys(measurements).length === 0) {
        return <>N/A</>;
    }

    return (
        <div className="measurements-container">
            <div className="measurement-header main-header" onClick={toggleMainSection}>
                <strong>Measurements</strong>
                <i className={`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
            </div>

            {isExpanded && (
                <div className="measurement-content">
                    <table className="measurement-table">
                        <tbody>
                            {Object.entries(measurements).map(([key, value]) => (
                                <tr key={key}>
                                    <td>
                                        <strong>{key}</strong>
                                    </td>
                                    <td>
                                        {typeof value === 'object' && value !== null ? (
                                            <pre>{JSON.stringify(value, null, 2)}</pre> // Display objects in readable format
                                        ) : value !== null && value !== undefined ? (
                                            value.toString()
                                        ) : (
                                            'N/A'
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const LegendChip = ({ label, color }) => (
    <span className="vb-legend__chip">
        <span className="vb-legend__swatches" aria-hidden>
            <span className="vb-legend__swatch" style={{ background: color }} />
        </span>
        {label}
    </span>
);

/**
 * The device's settled delivery reports, drawn the way the battery page draws its own: one
 * chart per market the device's schedule was booked against — committed power, available
 * capacity, setpoint following score, the FCR-activated periods washed behind them — plus
 * the endurance chart where the wire carries the per-market figures.
 */
const DeviceDeliveryCharts = ({ reports, from, to }) => {
    const chartMarkets = marketsWithCommitment(reports);
    return (
        <>
            {(chartMarkets.length > 0 ? chartMarkets : [null]).map(market => {
                const activated = market === null ? null : fcrActivatedSummary(reports, market);
                const marketRows = market === null ? null : toRows(reports, market);
                const hasEndurance = marketRows !== null && marketRows.some(row => row.enduranceH !== null);
                return (
                    <div key={market?.key ?? 'device'} className="vb-chart">
                        {chartMarkets.length > 1 ? (
                            <h3 className="vb-chart__market" style={{ color: market.color }}>
                                {market.label}
                            </h3>
                        ) : null}
                        {activated !== null ? (
                            <p className="vb-summary">
                                FCR activated periods: {activated.activatedCount} of {activated.measuredCount} measured
                                {market !== null && chartMarkets.length <= 1 ? ` · ${market.label}` : ''}
                            </p>
                        ) : null}
                        <DeliveryChart reports={reports} from={from} to={to} market={market} />
                        {hasEndurance ? (
                            <>
                                <h4 className="vb-chart__market">Endurance</h4>
                                <EnduranceChart rows={marketRows} from={from} to={to} />
                            </>
                        ) : null}
                    </div>
                );
            })}
            <div className="vb-legend">
                <LegendChip label="Activated" color={DELIVERY_SERIES_COLOR.activated} />
                {reports.some(r => r.measurement === 'out_of_service') ? <LegendChip label="Out of service" color={STATE_GREY_SWATCH_OOS} /> : null}
                {reports.some(r => r.measurement === 'unmeasured') ? <LegendChip label="Unmeasured" color={STATE_GREY_SWATCH_UNMEASURED} /> : null}
                <span className="vb-legend__note">Washes behind the chart: Activated marks the FCR activated periods. Times are UTC.</span>
            </div>
        </>
    );
};

const VirtualBattery = ({ device, reports, reportRange }) => {
    const history = useHistory();
    const infoData = device?.info?.data || {};
    const lifeCycleData = device?.lifeEvents?.data || [];
    const reportData = reports?.data || [];
    const marketCapacities = infoData.market_capacities || {};

    const deliveredReports = useMemo(() => reportData.map(deviceDeliveredFromWire), [reportData]);

    const sortedLifeCycleData = useMemo(() => {
        return [...lifeCycleData].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, [lifeCycleData]);

    const eventsModifier = sortedLifeCycleData.length === 1 ? 'vbatt-device__events-list--single' : '';

    const navigateToDevices = () => {
        history.push(`/batteries`);
    };

    return (
        <div className="vbatt-device">
            <i className="fa-solid fa-arrow-left link-icon devices-health__back-icon" onClick={() => navigateToDevices()}></i>
            <div className="vbatt-device__grid-row">
                <div className="vbatt-device__card">
                    <h2 className="vbatt-device__card-title">Device Info</h2>
                    <p className="vbatt-device__card-text">
                        <strong>Device ID:</strong> {infoData.id || 'N/A'}
                    </p>
                    <p className="vbatt-device__card-text">
                        <strong>Virtual Battery Name:</strong> {infoData.vbatt_name || 'N/A'}
                    </p>
                    <p className="vbatt-device__card-text">
                        <strong>Price Area:</strong> {infoData.price_area || 'N/A'}
                    </p>
                    <p className="vbatt-device__card-text">
                        <strong>Manufacturer:</strong> {infoData.manufacturer || 'N/A'}
                    </p>
                    <p className="vbatt-device__card-text">
                        <strong>Product Type:</strong> {infoData.product_type || 'N/A'}
                    </p>
                    <p className="vbatt-device__card-text">
                        <strong>Connection Status:</strong> <ConnectionStatus connected={infoData.connected} />
                    </p>
                    <p className="vbatt-device__card-text">
                        <strong>Last Updated:</strong> {infoData.updated ? new Date(infoData.updated).toLocaleString() : 'N/A'}
                    </p>
                    <div className="vbatt-device__card-text">
                        <div className="vbatt-device__measurements">
                            <MeasurementsDisplay measurements={infoData.measurements} />
                        </div>
                    </div>
                    {device.id && (
                        <div>
                            <Link to={`/vbatt-devices/${device.id}/health`} className="vbatt-device__btn">
                                Device Health Status
                            </Link>
                        </div>
                    )}
                </div>

                <div className="vbatt-device__card">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {Object.keys(marketCapacities).length > 0 && (
                            <div>
                                <h2 className="vbatt-device__card-title">Prequalified capacity</h2>
                                <table className="vbatt-device__markets-table">
                                    <thead>
                                        <tr>
                                            <th>Market</th>
                                            <th>Capacity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* The stored values are MW (a typical device holds 0.05 MW = 50 kW),
                                            never percentages. Zero and null both mean the device is not
                                            registered for the market, and say so honestly. */}
                                        {CAPACITY_MARKETS.map(market => (
                                            <tr key={market.key}>
                                                <td>{market.label}</td>
                                                <td>{fmtCapacityMw(marketCapacities[market.key])}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div>
                            <h2 className="vbatt-device__card-title">Lifecycle Events</h2>
                            {sortedLifeCycleData.length === 0 ? (
                                <p className="vbatt-device__card-text">No events found.</p>
                            ) : (
                                <div className={`vbatt-device__events-list ${eventsModifier}`}>
                                    {sortedLifeCycleData.map((eventItem, index) => (
                                        <div className="vbatt-device__event-item" key={index}>
                                            <p className="vbatt-device__event-title">
                                                <strong>{new Date(eventItem.timestamp).toLocaleString()}:</strong> {eventItem.event}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="vbatt-device__card">
                <h2 className="vbatt-device__card-title">Delivery Reports</h2>

                {reportData.length === 0 ? (
                    <p style={{ marginTop: '20px' }}>No delivery reports found for selected period.</p>
                ) : (
                    <DeviceDeliveryCharts reports={deliveredReports} from={reportRange.from} to={reportRange.to} />
                )}
            </div>
        </div>
    );
};

export { VbattDeviceSafetyWrapper as VbattDevicePage };
export default withUrlParam('id')(VbattDeviceSafetyWrapper);
